import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Circle, ArrowLeft, PartyPopper, Languages, Loader2 } from "lucide-react";
import { langName } from "@/components/Translate";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { t, getLang } from "@/i18n";

function Tick({ done, onClick, children, testid, big }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full text-left flex gap-3 items-start rounded-xl border transition-colors ${big ? "p-5" : "p-3"} ${done ? "border-[var(--border-active)] bg-[var(--accent-subtle)]" : "border-[var(--border-color)] hover:border-[var(--border-active)]"}`}>
      {done ? <CheckCircle2 className="text-[var(--accent-primary)] shrink-0" size={big ? 26 : 20} /> : <Circle className="faint shrink-0" size={big ? 26 : 20} />}
      <span className={`${big ? "text-lg" : "text-sm"} ${done ? "line-through faint" : ""}`}>{children}</span>
    </button>
  );
}

const toggle = (set, i) => set((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));

export default function Checklist() {
  const { id } = useParams();
  const api = useApi();
  const [cl, setCl] = useState(null);
  const [err, setErr] = useState("");
  const [mats, setMats] = useState([]);
  const [steps, setSteps] = useState([]);
  const [note, setNote] = useState("");
  const [started] = useState(new Date().toISOString());
  const [saved, setSaved] = useState(null);
  const [tr, setTr] = useState(null);
  const [trState, setTrState] = useState("idle");
  const [original, setOriginal] = useState(false);

  useEffect(() => { api.get(`/sops/${id}/checklist`).then((r) => setCl(r.data)).catch((e) => setErr(errMsg(e))); }, [api, id]);
  useEffect(() => {
    if (getLang() === "en") return;
    setTrState("loading");
    api.get(`/sops/${id}/checklist/translation`, { params: { lang: getLang() } })
      .then((r) => { setTr(r.data.translated ? r.data : null); setTrState("done"); })
      .catch(() => setTrState("error"));
  }, [api, id]);
  if (err) return <p className="page" data-testid="checklist-error">{err}</p>;
  if (!cl) return <p className="page faint">{t("Loading checklist…")}</p>;
  const total = cl.steps.length + cl.materials.length;
  const pct = total ? Math.round(((steps.length + mats.length) / total) * 100) : 0;
  const current = cl.steps.findIndex((_, i) => !steps.includes(i));
  const view = tr && !original ? tr : cl;

  const finish = async () => {
    try {
      const { data } = await api.post("/checklist-runs", { sop_id: id, checked_steps: steps, checked_materials: mats, started_at: started, note });
      setSaved(data);
      toast.success(t("Shift checklist saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (saved) return (
    <div className="page max-w-xl text-center stack" data-testid="checklist-saved">
      <PartyPopper className="mx-auto text-[var(--accent-primary)]" size={40} />
      <h1 className="text-3xl font-extrabold">{t(saved.complete ? "All steps done." : "Checklist saved.")}</h1>
      <p className="muted">{t("{a}/{b} steps", { a: saved.checked_steps.length, b: saved.total_steps })} · {t("{a}/{b} materials", { a: saved.checked_materials.length, b: saved.total_materials })} · {t("recorded for your owner.")}</p>
      <div className="flex gap-2 justify-center"><Link to={`/sops/${id}`} className="btn btn-ghost">{t("Back to SOP")}</Link><button className="btn btn-primary" onClick={() => window.location.reload()} data-testid="checklist-restart-button">{t("Start again")}</button></div>
    </div>
  );

  return (
    <div className="page max-w-3xl stack">
      <Link to={`/sops/${id}`} className="text-sm faint inline-flex items-center gap-1 hover:text-[var(--text-primary)]" data-testid="checklist-back-link"><ArrowLeft size={14} /> {view.title}</Link>
      <div className="rise">
        <p className="mono-label">{t("Shift checklist")} · v{cl.version} · {t("Updated")} {fmtDate(cl.updated_at)}</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1" data-testid="checklist-title">{view.title}</h1>
        {trState === "error" && <p className="text-xs faint mt-2" data-testid="checklist-translate-error">{t("Translation is unavailable right now. Showing the original.")}</p>}
        {trState === "loading" && <p className="text-xs faint flex items-center gap-1.5 mt-2" data-testid="checklist-translating"><Loader2 size={12} className="animate-spin" /> {t("Translating…")}</p>}
        {tr && (
          <p className="text-xs faint flex flex-wrap items-center gap-1.5 mt-2" title={t("Translations are for accessibility; original text remains available.")} data-testid="checklist-translate-label">
            <Languages size={12} /> {t(original ? "Original in {lang}" : "Translated from {lang}", { lang: langName("en") })}
            <button className="text-[var(--accent-primary)] font-semibold hover:underline" onClick={() => setOriginal(!original)} data-testid="checklist-translate-toggle">{t(original ? "View translated" : "View original")}</button>
          </p>
        )}
      </div>
      <div className="sticky top-16 z-30 bg-[var(--bg-primary)] py-3 stack-sm">
        <div className="flex justify-between text-sm"><span className="muted">{t("Progress")}</span><span className="font-bold" data-testid="checklist-progress-text">{pct}%</span></div>
        <Progress value={pct} className="h-2" data-testid="checklist-progress" />
      </div>
      {cl.materials.length > 0 && (
        <div className="card stack-sm">
          <p className="mono-label">1 · {t("Gather materials")}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {view.materials.map((m, i) => <Tick key={i} done={mats.includes(i)} onClick={() => toggle(setMats, i)} testid={`checklist-material-${i}`}><b>{m.item}</b> — {m.quantity}</Tick>)}
          </div>
        </div>
      )}
      <div className="card stack-sm">
        <p className="mono-label">2 · {t("Follow the steps")}</p>
        {view.steps.map((s, i) => <Tick key={i} big={i === current} done={steps.includes(i)} onClick={() => toggle(setSteps, i)} testid={`checklist-step-${i}`}><span className="faint mr-2">{i + 1}.</span>{s}</Tick>)}
      </div>
      <div className="card stack-sm">
        <label className="mono-label">{t("Note for the owner (optional)")}</label>
        <input className="field" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("e.g. Ran out of cinnamon")} data-testid="checklist-note-input" />
        <button className="btn btn-primary w-full" onClick={finish} disabled={!steps.length && !mats.length} data-testid="checklist-finish-button">{t("Finish & save checklist")}</button>
      </div>
    </div>
  );
}
